import { logToServer } from '@/services/LogService';
import { Notification, pushNotification } from '@/state/reducers/notificationSlice';
import { store } from '@/state/store';
import { getUniversalInstrument } from '@/utils/hooks/useInstrument';
import { getAssetName } from '@/utils/symbolMapping';
import BigNumber from 'bignumber.js';
import { v4 as uuidv4 } from 'uuid';
import { Side } from '../compiled_proto/com/celertech/marketdata/api/enums/SideProto';
import { ExecutionType } from '../compiled_proto/com/celertech/orderrouting/api/enums/ExecutionTypeProto';
import { OrderType } from '../compiled_proto/com/celertech/orderrouting/api/enums/OrderTypeProto';
import { TimeInForceType } from '../compiled_proto/com/celertech/orderrouting/api/enums/TimeInForceTypeProto';
import { FxOrderSnapshotDownstreamEvent } from '../compiled_proto/com/celertech/orderrouting/api/order/DownstreamOrderProto';
import { BlotterItem } from '../model/blotters';
import { capitalize, capitalizeAll, getQtyFormatter } from '../utils/format';
import { getEnvironment } from './environmentHelper';
import { FilledBar, OCOReference, titlePrice } from './orderStatusHelper';
// import { getOtherCurrency } from './currencyHelper';

export interface OrderEvent {
    orderId: string;
    price: number;
    qty: number;
}

export const toastExecutionType = (previousEvents: BlotterItem[], event: FxOrderSnapshotDownstreamEvent) => {
    const toast = getExecutionTypeToastConfig(previousEvents, event);
    if (toast) {
        store.dispatch(pushNotification(toast.notification as Notification));
        if (getEnvironment() !== 'prod') Promise.all([toast.logs()]);
    }
};

export const getExecutionTypeToastConfig = (previousEvents: BlotterItem[], event: FxOrderSnapshotDownstreamEvent) => {
    const {
        // securityId,
        qty,
        price,
        avgPx,
        cumQty,
        stopPx,
        orderId,
        id,
        currency,
        // leg,
        securityCode,
        orderType,
        orderStatus,
        executionType,
        side: orderSide,
        timeInForce,
        orderMetadata
        // lastQty,
        // cumQty,
        // leavesQty
    } = event;

    const instrumentConfig = getUniversalInstrument(securityCode);

    const previousEvent = previousEvents.find((event) => event.orderId === orderId);

    const type = OrderType[orderType] as keyof typeof OrderType;
    const side = Side[orderSide] as keyof typeof Side;
    const oppSide = Side[orderSide === 1 ? 2 : 1] as keyof typeof Side;
    const execution_type = ExecutionType[executionType] as keyof typeof ExecutionType;
    const slippage = orderMetadata.find((meta) => meta.key === 'SLIPPAGE')?.value || 0;

    // let effectivePrice = price;

    // if (orderType === OrderType.LIMIT) {
    //     if (orderSide === Side.BUY) {
    //         effectivePrice = new BigNumber(previousEvent?.price || 0).plus(slippage).toNumber();
    //     }
    //     if (orderSide === Side.SELL) {
    //         effectivePrice = new BigNumber(previousEvent?.price || 0).minus(slippage).toNumber();
    //     }
    // }

    const text = {
        orderType: `${capitalizeAll(type)} Order`,
        side: capitalize(side),
        oppSide: capitalize(oppSide),
        tif: TimeInForceType[timeInForce].toString(),
        titlePrice: titlePrice(orderType, previousEvent?.price || 0, previousEvent?.stopPrice || 0, slippage)
    };

    const strategy = orderMetadata.find(({ key }) => key === 'ORIGINAL_STRATEGY_ORDER_TYPE')?.value || '';
    const parentStrategy = orderMetadata.find(({ key }) => key === 'PARENT_STRATEGY_ID')?.value || '';
    const isPartOfOcoOrder = !!(strategy || parentStrategy);
    const formattedParentStrategy = parentStrategy.replace('STRATEGYORDER:PARENT:', '');

    const formatQty = getQtyFormatter();

    const fmtAvgPx = formatQty.format(avgPx);
    const fmtPrice = formatQty.format(price);
    const fmtQuantity = formatQty.format(qty);
    const fmtStopPrice = formatQty.format(stopPx);
    const fmtPrevQuantity = formatQty.format(previousEvent?.qty || 0);
    const fmtAccumulatedAmount = formatQty.format(cumQty);
    // const fmtLegPrice = formatQty.format(leg[0]?.price);
    // const fmtFilledAmount = formatQty.format(lastQty);
    // const fmtRemainingAmount = formatQty.format(leavesQty);

    let ccy = getAssetName(currency);
    if (['Future', 'Commodity', 'Index'].includes(instrumentConfig?.type)) {
        ccy = '';
    }
    const [ccy1, ccy2] = securityCode.split('/');

    const ccy2Order = currency === ccy2;
    const ccy1AssetName = getAssetName(ccy1);
    const ccy2AssetName = getAssetName(ccy2);

    let position = '';
    if (ccy2Order) position = `${ccy2AssetName} vs ${ccy1AssetName}`;
    else {
        if (['Commodity', 'Index'].includes(instrumentConfig?.type)) {
            position = instrumentConfig.tsSymbol;
        } else if (['Future'].includes(instrumentConfig?.type)) {
            position = instrumentConfig.gcexSymbol;
            // position = `${ccy1AssetName} vs ${instrumentConfig.ccy2 || 'USD'}`;
        } else position = `${ccy1AssetName} vs ${ccy2AssetName}`;
    }

    const title = `${text.orderType} to ${text.side} ${fmtPrevQuantity} ${position} ${text.titlePrice}`;

    const showPrice = () => {
        switch (orderType) {
            case OrderType.LIMIT:
                return price !== previousEvent?.price && `Amended Price: ${fmtPrice}\n`;
            case OrderType.STOP_LIMIT:
                return stopPx !== previousEvent?.stopPrice && `Amended Price: ${fmtStopPrice}\n`;
            case OrderType.STOP_MARKET:
                return stopPx !== previousEvent?.stopPrice && `Amended Price: ${fmtStopPrice}\n`;
            default:
                return '';
        }
    };

    switch (execution_type) {
        case 'REPLACED': {
            const amendedPrice = showPrice();

            return {
                logs: async () =>
                    logToServer('info', JSON.stringify({ message: `${title} Amended [${event.account}]`, event })),
                notification: {
                    id: uuidv4(),
                    account: event.account,
                    type: 'success',
                    body: (
                        <div>
                            {/* TBA - Translations */}
                            {amendedPrice && <p>{amendedPrice}</p>}
                            {qty !== previousEvent?.qty && (
                                <p>
                                    Amended Amount: {fmtQuantity} {ccy}
                                </p>
                            )}
                            <p>OrderId: {orderId}</p>
                            <p>Exec Id: {id}</p>

                            {isPartOfOcoOrder && <OCOReference formattedParentStrategy={formattedParentStrategy} />}
                            <FilledBar
                                qty={fmtQuantity}
                                avgPx={fmtAvgPx}
                                cumQty={fmtAccumulatedAmount}
                                slippage={slippage.toString()}
                                progress={BigNumber(cumQty)
                                    .dividedBy(qty || 1)
                                    .multipliedBy(100)
                                    .toNumber()}
                            />
                        </div>
                    ),
                    title: `${title} Amended`,
                    created_at: new Date(),
                    isPartOfOcoOrder,
                    seen: false
                }
            };
        }
        default:
            break;
    }
};
